import { useMemo, useState } from 'react';
import {
  HeaderGroup,
  useFlexLayout,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';
import { ReactComponent as SortArrow } from 'assets/icons/sort_arrow.svg';
import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import usePaymentAuthorizations from 'hooks/usePaymentAuthorizations';
import Pagination, { ITablePaginate } from 'components/Tables/Pagination';
import { formatUsdCurrency } from 'utils/formatUsdCurrency';
import { datetimeFormat, DATE_TIME_FORMAT } from 'utils/datetime';
import { IPaymentAuthorization, PaymentStatus } from 'interfaces/stripe';
import SelectDropdown, {
  SelectDropdownOption,
} from 'components/SelectDropdown/SelectDropdown';
import AppointmentDateRange from 'components/AppointmentDateRange/AppointmentDateRange';
import {
  CALENDAR_TYPE,
  getDateRangeByCalendarType,
} from 'utils/getDateRangeByCalendarType';
import { loadSelectedClinicId } from 'utils/storage';
import PaymentStatusBadge from './PaymentStatusBadge';
import DisputeDetail, { DisputeDetailTooltip } from './DisputeDetail';
import FailedDetail, { FailedDetailTooltip } from './FailedDetail';
import ThreeDotsOption from './ThreeDotsOption';
import moment from 'moment';

const defaultTableState = {
  currentPage: 1,
  pageSize: 10,
  sort: 'desc' as const,
  sortBy: 'createdAt',
};

const PAYMENT_STATUS_OPTIONS: SelectDropdownOption[] = [
  { label: 'All', value: 'all' },
  { label: 'Held', value: PaymentStatus.HELD },
  { label: 'Charged', value: PaymentStatus.CAPTURED },
  { label: 'Released', value: PaymentStatus.RELEASED },
  { label: 'Expired', value: PaymentStatus.AUTH_EXPIRED },
  { label: 'Refunded', value: PaymentStatus.REFUNDED },
  { label: 'Failed', value: PaymentStatus.FAILED },
  { label: 'Disputed', value: PaymentStatus.DISPUTED },
];

// Deterministic avatar colour so the same patient keeps the same swatch.
const AVATAR_COLORS = ['#B01BAE', '#DC5C89', '#4A1169', '#0D9488', '#C4B5FD'];

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
};

const getAvatarColor = (name: string) => {
  const sum = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const AppointmentHoldsTable = () => {
  const [currentPage, setCurrentPage] = useState(defaultTableState.currentPage);
  const [pageSize, setPageSize] = useState(defaultTableState.pageSize);
  const [sort, setSort] = useState<'asc' | 'desc'>(defaultTableState.sort);
  const [sortBy, setSortBy] = useState(defaultTableState.sortBy);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | 'all'>(
    'all'
  );
  const selectedClinicId = loadSelectedClinicId();
  const [selectedCalendarType, setSelectedCalendarType] = useState(
    CALENDAR_TYPE.THIS_MONTH
  );
  const [dateRange, setDateRange] = useState<{
    fromDate: string;
    toDate: string;
  }>(
    () =>
      getDateRangeByCalendarType(CALENDAR_TYPE.THIS_MONTH) as {
        fromDate: string;
        toDate: string;
      }
  );

  const { data, isLoading, mutate } = usePaymentAuthorizations({
    page: currentPage,
    limit: pageSize,
    sort,
    sortBy,
    paymentStatus: paymentStatus === 'all' ? undefined : paymentStatus,
    startDate: moment(dateRange.fromDate, 'YYYY-MM-DD')
      .startOf('days')
      .utc(true)
      .format(),

    endDate: moment(dateRange.toDate, 'YYYY-MM-DD')
      .endOf('days')
      .utc(true)
      .format(),
  });

  const total = data?.metadata?.total ?? 0;

  const dataTable = useMemo(() => {
    if (isLoading || !data?.data) {
      return { data: [] as IPaymentAuthorization[], metadata: null };
    }

    return { data: data.data, metadata: data.metadata };
  }, [data?.data, data?.metadata, isLoading]);

  const columns = useMemo(
    () => [
      {
        Header: 'APPT DATE & TIME',
        id: 'appointmentDate',
        width: 140,
        accessor: (row: IPaymentAuthorization) => (
          <>
            <div className="text-14 text-darkest-grey whitespace-nowrap">
              {datetimeFormat({
                dateString: String(row.appointmentDate),
                format: DATE_TIME_FORMAT.APPOINTMENT_DATE,
                pattern: DATE_TIME_FORMAT.MMM_DD_YYYY,
              })}
            </div>
            <div className="text-12 text-[#9CA3AF]">
              {datetimeFormat({
                dateString: row.startTime,
                format: DATE_TIME_FORMAT.HH_MM_SS,
                pattern: DATE_TIME_FORMAT.HH_MM_A,
              })}
            </div>
          </>
        ),
      },
      {
        Header: 'PATIENT',
        id: 'patientName',
        width: 180,
        accessor: (row: IPaymentAuthorization) => {
          const patientName = row.patient?.name ?? '';
          return (
            <div className="flex items-center gap-x-0.8 mr-[20px]">
              <span
                className="w-2.8 h-2.8 rounded-full flex items-center justify-center text-white text-11 font-bold flex-shrink-0"
                style={{ backgroundColor: getAvatarColor(patientName) }}
              >
                {getInitials(patientName)}
              </span>
              <span className="text-14 text-darkest-grey words-break">
                {patientName}
              </span>
            </div>
          );
        },
      },
      {
        Header: 'PRACTITIONER',
        id: 'doctorName',
        width: 150,
        accessor: (row: IPaymentAuthorization) => (
          <span className="text-14 text-darkest-grey whitespace-nowrap">
            {row.doctor?.name ?? ''}
          </span>
        ),
      },
      {
        Header: 'SERVICE',
        id: 'serviceName',
        width: 180,
        accessor: (row: IPaymentAuthorization) => (
          <span className="text-14 text-darkest-grey break-words">
            {row.service?.name ?? ''}
          </span>
        ),
      },
      {
        Header: 'REQUESTED TIME',
        id: 'requestedAt',
        width: 130,
        accessor: (row: IPaymentAuthorization) => (
          <>
            <div className="text-14 text-darkest-grey whitespace-nowrap">
              {datetimeFormat({
                dateString: row.requestedAt,
                pattern: DATE_TIME_FORMAT.MMM_DD_YYYY,
                timezone: row.timezone,
              })}
            </div>
            <div className="text-12 text-[#9CA3AF] whitespace-nowrap">
              {datetimeFormat({
                dateString: row.requestedAt,
                pattern: DATE_TIME_FORMAT.HH_MM_A,
                timezone: row.timezone,
              })}
            </div>
          </>
        ),
      },
      {
        Header: 'HOLD',
        id: 'holdAmount',
        width: 90,
        accessor: (row: IPaymentAuthorization) => (
          <span className="text-14 text-darkest-grey whitespace-nowrap">
            {row.holdAmount != null ? formatUsdCurrency(row.holdAmount) : '-'}
          </span>
        ),
      },
      {
        Header: 'STATUS',
        id: 'status',
        width: 110,
        accessor: (row: IPaymentAuthorization) => (
          <>
            <PaymentStatusBadge
              status={row.isDisputed ? PaymentStatus.DISPUTED : row.status}
              tooltipId={
                row.isDisputed && row.dispute
                  ? `dispute-${row.appointmentId}`
                  : row.status === PaymentStatus.FAILED &&
                    row.stripeFailureReason
                  ? `failed-${row.appointmentId}`
                  : undefined
              }
              tooltip={
                row.isDisputed && row.dispute ? (
                  <DisputeDetailTooltip
                    dispute={row.dispute}
                    timezone={row.timezone}
                  />
                ) : row.status === PaymentStatus.FAILED &&
                  row.stripeFailureReason ? (
                  <FailedDetailTooltip
                    reason={row.stripeFailureReason}
                    operation={row.failedOperation}
                  />
                ) : undefined
              }
            />
            {row.isDisputed && row.dispute && (
              <DisputeDetail dispute={row.dispute} timezone={row.timezone} />
            )}
            {row.status === PaymentStatus.FAILED &&
              row.stripeFailureReason && (
                <FailedDetail reason={row.stripeFailureReason} />
              )}
          </>
        ),
      },
      {
        Header: 'HOLD EXPIRES AT',
        id: 'holdExpiresAt',
        width: 130,
        accessor: (row: IPaymentAuthorization) =>
          row.holdExpiresAt ? (
            <>
              <div className="text-14 text-darkest-grey whitespace-nowrap">
                {datetimeFormat({
                  dateString: row.holdExpiresAt,
                  pattern: DATE_TIME_FORMAT.MMM_DD_YYYY,
                  timezone: row.timezone,
                })}
              </div>
              <div className="text-12 text-[#9CA3AF] whitespace-nowrap">
                {datetimeFormat({
                  dateString: row.holdExpiresAt,
                  pattern: DATE_TIME_FORMAT.HH_MM_A,
                  timezone: row.timezone,
                })}
              </div>
            </>
          ) : (
            <span className="text-14 text-darkest-grey whitespace-nowrap">
              -
            </span>
          ),
      },
      {
        Header: '',
        id: 'action',
        width: 90,
        accessor: (row: IPaymentAuthorization) => (
          <ThreeDotsOption authorization={row} />
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns,
        data: dataTable.data,
        manualPagination: true,
        pageCount: dataTable.metadata ? total : 0,
      },
      useSortBy,
      useFlexLayout,
      usePagination
    );

  const handleSortColumn = async (
    column: HeaderGroup<IPaymentAuthorization>
  ) => {
    setCurrentPage(defaultTableState.currentPage);
    setSortBy(column.id);
    if (column.id === sortBy) {
      return setSort((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    }
    setSort('asc');
    await mutate(undefined, { revalidate: false });
  };

  const handlePaginating = async (tableData: ITablePaginate) => {
    setCurrentPage(tableData.perPage === pageSize ? tableData.currPage : 1);
    setPageSize(tableData.perPage);
    await mutate(undefined, { revalidate: false });
  };

  const getLabel = () => {
    const currentValue = PAYMENT_STATUS_OPTIONS.find(
      (option) => option.value === paymentStatus
    )!;
    return currentValue.label.toLowerCase();
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={columns.length}>
            <div className="w-full h-[37rem] flex justify-center items-center">
              <LoadingSpinner className="all-child:fill-magenta" />
            </div>
          </td>
        </tr>
      );
    }

    if (page.length === 0) {
      return (
        <tr>
          <td colSpan={columns.length}>
            <div className="w-full h-[24rem] flex flex-col gap-y-1.6 justify-center items-center text-14 text-darkest-grey">
              <EmptyBucket />
              <span>No appointments with {getLabel()} yet.</span>
            </div>
          </td>
        </tr>
      );
    }

    return page.map((row) => {
      prepareRow(row);
      return (
        <tr
          {...row.getRowProps()}
          className="bg-white border-b border-light-grey last:border-0 group hover:bg-lightPink cursor-pointer"
          key={row.original.appointmentId}
        >
          {row.cells.map((cell, index) => (
            <td
              {...cell.getCellProps()}
              className={`text-left py-1.9 my-auto ${
                cell.column.id === 'action'
                  ? 'flex justify-end pr-2.4'
                  : 'px-1.6'
              }`}
              key={`cell-${index}`}
            >
              {cell.render('Cell')}
            </td>
          ))}
        </tr>
      );
    });
  };

  return (
    <>
      <div className=" flex items-center justify-between gap-x-2.4 mb-2.4 p-2">
        <h4 className="text-18 font-bold text-darkest-grey">
          Appts With Holds <span className="text-magenta">({total})</span>
        </h4>
        <div className="flex items-center gap-x-1.6">
          <div className="w-[22rem]">
            <SelectDropdown
              options={PAYMENT_STATUS_OPTIONS}
              value={paymentStatus}
              onChange={(value) => {
                setPaymentStatus(value as PaymentStatus | 'all');
                setCurrentPage(1);
              }}
            />
          </div>
          <AppointmentDateRange
            clinicIds={[selectedClinicId]}
            dateRange={dateRange}
            onChangeDateRange={(range) => {
              setDateRange(range);
              setCurrentPage(1);
            }}
            selectedCalendarType={selectedCalendarType}
            setSelectedCalendarType={setSelectedCalendarType}
          />
        </div>
      </div>

      <table {...getTableProps()} className="w-full text-14 leading-[2.1rem]">
        <thead className="font-bold">
          {headerGroups.map((headerGroup, index) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              className="border-b border-light-grey"
              key={`header-group-${index}`}
            >
              {headerGroup.headers.map((column, index) => (
                <th
                  {...column.getHeaderProps()}
                  className="text-left py-1.9 px-1.6 whitespace-nowrap"
                  key={`header-${index}`}
                >
                  {column.id === 'action' ? null : (
                    <div
                      className="flex items-center gap-x-0.9 cursor-pointer"
                      onClick={() => handleSortColumn(column)}
                    >
                      <span className="uppercase text-dim-grey text-11 leading-[1.8rem]">
                        {column.render('Header')}
                      </span>
                      <SortArrow
                        className={`w-1.2 shrink-0 h-1.2 transition-all ${
                          column.id === sortBy
                            ? 'fill-magenta'
                            : 'all-child:fill-[#9CA3AF]'
                        } ${
                          column.id === sortBy && sort === 'asc' && 'rotate-180'
                        }`}
                      />
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>{renderTableBody()}</tbody>
      </table>

      {dataTable.metadata && total > 0 && (
        <div className="py-1.6">
          <Pagination
            isBorderNotIncluded
            handlePaginating={handlePaginating}
            data={{ currPage: currentPage, perPage: pageSize, total }}
          />
        </div>
      )}
    </>
  );
};

export default AppointmentHoldsTable;
